import { useEffect, useState } from 'react';
import {
  Box, Stack, Typography, Divider, Switch, Avatar, Chip, Button, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, ToggleButtonGroup, ToggleButton, Tooltip,
} from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import KeyboardOutlinedIcon from '@mui/icons-material/KeyboardOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { PmCard, PmInfoBox, PmPill } from '../components/pm';
import { AnforderungKategorienManager } from '../components/settings/AnforderungKategorienManager';
import { KanbanSettingsManager } from '../components/settings/KanbanSettingsManager';
import { useAuth } from '../context/AuthContext';
import { useThemeMode } from '../context/ThemeContext';
import { usePermissionContext } from '../context/PermissionContext';
import { ApiError, formatApiError } from '../api/client';
import {
  fetchCompanyLogo, uploadCompanyLogo, deleteCompanyLogo, validateLogoFile, readLegacyLogo, clearLegacyLogo,
  MAX_LOGO_BYTES, LOGO_MIME_TYPES, LOGO_PERMISSION,
} from '../lib/pdf/companyLogo';
import type { ReportLogo } from '../lib/pdf/reportModel';

interface NotifyPrefs {
  toastSave: boolean;
  toastError: boolean;
  toastDelete: boolean;
  highlightChanges: boolean;
}

const PREFS_KEY = 'pm:notify-prefs';
const DEFAULT_PREFS: NotifyPrefs = {
  toastSave: true,
  toastError: true,
  toastDelete: true,
  highlightChanges: false,
};

function readPrefs(): NotifyPrefs {
  if (typeof window === 'undefined') return DEFAULT_PREFS;
  try {
    const raw = window.localStorage.getItem(PREFS_KEY);
    if (!raw) return DEFAULT_PREFS;
    return { ...DEFAULT_PREFS, ...(JSON.parse(raw) as Partial<NotifyPrefs>) };
  } catch { return DEFAULT_PREFS; }
}

interface SettingRowProps {
  label: string;
  description?: string;
  control: React.ReactNode;
  disabled?: boolean;
  badge?: string;
}

function SettingRow({ label, description, control, disabled, badge }: SettingRowProps) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
      sx={{ py: 1.25, opacity: disabled ? 0.55 : 1 }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--pm-fg-base)' }}>{label}</Typography>
          {badge && <Chip size="small" label={badge} sx={{ height: 18, fontSize: '0.65rem', background: 'var(--pm-bg-deep)', color: 'var(--pm-fg-muted)' }} />}
          {disabled && (
            <Tooltip title="Kommt in einer späteren Welle">
              <LockOutlinedIcon sx={{ fontSize: 14, color: 'var(--pm-fg-subtle)' }} />
            </Tooltip>
          )}
        </Stack>
        {description && <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)', display: 'block', mt: 0.25 }}>{description}</Typography>}
      </Box>
      <Box sx={{ flexShrink: 0 }}>{control}</Box>
    </Stack>
  );
}

function SectionCard({ icon, title, hint, children }: { icon: React.ReactNode; title: string; hint?: string; children: React.ReactNode }) {
  return (
    <PmCard>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
        {icon}
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
          {hint && <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)' }}>{hint}</Typography>}
        </Box>
      </Stack>
      <Divider sx={{ mb: 1, opacity: 0.4 }} />
      <Stack divider={<Divider sx={{ opacity: 0.25 }} />}>{children}</Stack>
    </PmCard>
  );
}

/**
 * Firmenlogo für PDF-Exporte (PHQ-92), pro Mandant in der pm-api gespeichert.
 * Hochladen und Entfernen verlangt das Recht `admin.settings`; ohne es wird nur
 * angezeigt. Ein altes Browser-Logo wird einmalig übernommen (siehe unten).
 */
function PdfLogoSetting({ tenantId }: { tenantId: string | undefined }) {
  const { hasPermission } = usePermissionContext();
  const canEdit = hasPermission(LOGO_PERMISSION);
  const [logo, setLogo] = useState<ReportLogo | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let current = await fetchCompanyLogo();
        // Einmalige Übernahme: Altbestand im Browser, auf dem Server keins, Benutzer darf schreiben.
        // Erst nach erfolgreichem Upload wird der lokale Eintrag gelöscht; sonst bleibt er für den nächsten Versuch.
        if (!current && canEdit) {
          const legacy = readLegacyLogo(tenantId);
          if (legacy) {
            await uploadCompanyLogo(legacy, legacy.type === 'image/png' ? 'logo.png' : 'logo.jpg');
            clearLegacyLogo(tenantId);
            current = await fetchCompanyLogo();
          }
        }
        if (!cancelled) setLogo(current);
      } catch (e) {
        if (!cancelled) setError(formatApiError(e));
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId, canEdit]);

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    try {
      validateLogoFile(file);
      await uploadCompanyLogo(file, file.name);
      setLogo(await fetchCompanyLogo());
    } catch (e) {
      const kind = e instanceof Error && !(e instanceof ApiError) ? e.message : '';
      setError(
        kind === 'type' ? 'Nur PNG- oder JPEG-Dateien werden unterstützt.'
          : kind === 'size' ? `Die Datei ist zu groß (maximal ${Math.round(MAX_LOGO_BYTES / 1024)} KB).`
            : formatApiError(e),
      );
    }
  };

  const remove = async () => {
    setError(null);
    try {
      await deleteCompanyLogo();
      setLogo(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  return (
    <Stack spacing={1.5} sx={{ py: 1 }}>
      <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)' }}>
        Das Logo erscheint in der Kopfzeile exportierter PDF-Berichte und gilt für den ganzen Mandanten.
        {!canEdit && ' Ändern können nur Benutzer mit dem Recht „Einstellungen verwalten“.'}
      </Typography>
      {logo && (
        <Box sx={{ p: 1, bgcolor: '#fff', borderRadius: 1, alignSelf: 'flex-start', border: '1px solid rgba(127,127,127,0.3)' }}>
          <img src={logo.dataUrl} alt="Firmenlogo" data-testid="pdf-logo-preview" style={{ display: 'block', maxWidth: 200, maxHeight: 80 }} />
        </Box>
      )}
      {error && <Alert severity="warning" data-testid="pdf-logo-error">{error}</Alert>}
      {canEdit && (
        <Stack direction="row" spacing={1}>
          <Button component="label" size="small" variant="outlined">
            {logo ? 'Logo ersetzen' : 'Logo hochladen'}
            <input
              hidden
              type="file"
              accept={LOGO_MIME_TYPES.join(',')}
              data-testid="pdf-logo-input"
              onChange={(e) => { void onFile(e.target.files?.[0]); e.target.value = ''; }}
            />
          </Button>
          {logo && (
            <Button size="small" color="inherit" onClick={() => void remove()} data-testid="pdf-logo-remove">
              Entfernen
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
}

const APP_VERSION = __APP_VERSION__; // = package.json version (Single Source, kein Drift)
const BACKEND_VERSION = '0.12.3';
const KLICKDUMMY_REF = 'sms_pm_crud_v3_9.html';

export function SettingsPage() {
  const { user, currentTenant } = useAuth();
  const { experimentalUnlocked, toggleExperimental } = useThemeMode();
  const [prefs, setPrefs] = useState<NotifyPrefs>(() => readPrefs());
  const [theme, setTheme] = useState<'dark' | 'light' | 'auto'>('dark');
  const [language, setLanguage] = useState<'de' | 'en'>('de');

  useEffect(() => {
    try { window.localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch { /* quota */ }
  }, [prefs]);

  const initials = user?.name
    ? user.name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('')
    : '?';

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <SettingsOutlinedIcon sx={{ fontSize: 28, color: 'var(--pm-fg-accent)' }} />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Einstellungen</Typography>
          <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)' }}>
            Persönliche Präferenzen und Anzeige-Optionen
          </Typography>
        </Box>
      </Stack>

      <PmInfoBox variant="info" title="MVP-Hinweis">
        Diese Seite zeigt eine erste Übersicht. Einige Optionen sind noch nicht persistiert (markiert mit Schloss-Symbol) und werden in folgenden Wellen angeschlossen. Aktive Schalter speichern lokal im Browser.
      </PmInfoBox>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 2 }}>
        {/* Profil */}
        <SectionCard
          icon={<PersonOutlineOutlinedIcon sx={{ color: 'var(--pm-fg-accent)' }} />}
          title="Profil"
          hint="Read-only — wird beim Login aus dem BFF gezogen"
        >
          <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 1 }}>
            <Avatar sx={{ width: 56, height: 56, bgcolor: 'var(--pm-fg-accent)', color: 'var(--pm-bg-deep)', fontWeight: 700 }}>
              {initials}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{user?.name ?? 'Unbekannt'}</Typography>
              <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)', fontFamily: 'monospace', display: 'block' }}>
                {user?.email ?? '—'}
              </Typography>
              {currentTenant && (
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                  <PmPill tone="primary">{currentTenant.name}</PmPill>
                  <PmPill tone="neutral">{currentTenant.role}</PmPill>
                </Stack>
              )}
            </Box>
          </Stack>
        </SectionCard>

        {/* Erscheinungsbild */}
        <SectionCard
          icon={<PaletteOutlinedIcon sx={{ color: 'var(--pm-fg-accent)' }} />}
          title="Erscheinungsbild"
          hint="Theme + Layout-Präferenzen"
        >
          <SettingRow
            label="Theme"
            description="Aktuell ist nur Dark-Theme produktiv. Light & Auto folgen."
            badge="Vorschau"
            disabled
            control={
              <ToggleButtonGroup
                size="small"
                exclusive
                value={theme}
                onChange={(_, v) => v && setTheme(v)}
                disabled
              >
                <ToggleButton value="dark">Dark</ToggleButton>
                <ToggleButton value="light">Light</ToggleButton>
                <ToggleButton value="auto">Auto</ToggleButton>
              </ToggleButtonGroup>
            }
          />
          <SettingRow
            label="Experimentelle Themes freischalten"
            description="Zeigt die verspielten Themes (Fußball, Unicorn, Space Trek, ASCII/CLI, Matrix) im Theme-Menü. Gespeichert im Browser."
            control={
              <Switch
                size="small"
                checked={experimentalUnlocked}
                onChange={toggleExperimental}
                slotProps={{ input: { 'aria-label': 'Experimentelle Themes freischalten' } }}
              />
            }
          />
          <SettingRow
            label="Kompakte Tabellenzeilen"
            description="Reduziert Padding in Listen — mehr Einträge pro Höhe"
            disabled
            control={<Switch size="small" disabled />}
          />
          <SettingRow
            label="Animationen reduzieren"
            description="Respektiert prefers-reduced-motion — derzeit immer an wenn das OS es signalisiert"
            disabled
            control={<Switch size="small" disabled defaultChecked />}
          />
        </SectionCard>

        {/* Sprache */}
        <SectionCard
          icon={<LanguageOutlinedIcon sx={{ color: 'var(--pm-fg-accent)' }} />}
          title="Sprache"
          hint="Oberflächentexte und Datumsformate"
        >
          <SettingRow
            label="Anzeigesprache"
            description="Default ist Deutsch (de). Weitere Sprachen liegen als Locale-Files bereit."
            badge="Vorschau"
            disabled
            control={
              <ToggleButtonGroup
                size="small"
                exclusive
                value={language}
                onChange={(_, v) => v && setLanguage(v)}
                disabled
              >
                <ToggleButton value="de">DE</ToggleButton>
                <ToggleButton value="en">EN</ToggleButton>
              </ToggleButtonGroup>
            }
          />
          <SettingRow
            label="Datumsformat"
            description="Aktuell DD.MM.YYYY · folgt der Sprache"
            disabled
            control={<Chip size="small" label="DD.MM.YYYY" sx={{ background: 'var(--pm-bg-deep)' }} />}
          />
          <SettingRow
            label="Erster Wochentag"
            description="Aktuell Montag (ISO 8601)"
            disabled
            control={<Chip size="small" label="Montag" sx={{ background: 'var(--pm-bg-deep)' }} />}
          />
        </SectionCard>

        {/* Benachrichtigungen */}
        <SectionCard
          icon={<NotificationsOutlinedIcon sx={{ color: 'var(--pm-fg-accent)' }} />}
          title="Benachrichtigungen"
          hint="Toast-Verhalten · gespeichert im Browser"
        >
          <SettingRow
            label="Toast bei „Gespeichert"
            description="Grüner Hinweis nach erfolgreichem Save"
            control={
              <Switch
                size="small"
                checked={prefs.toastSave}
                onChange={(_, v) => setPrefs((p) => ({ ...p, toastSave: v }))}
              />
            }
          />
          <SettingRow
            label="Toast bei Fehler"
            description="Roter Hinweis mit Backend-Detail (formatApiError)"
            control={
              <Switch
                size="small"
                checked={prefs.toastError}
                onChange={(_, v) => setPrefs((p) => ({ ...p, toastError: v }))}
              />
            }
          />
          <SettingRow
            label="Toast bei „Gelöscht"
            description="Bestätigung nach erfolgreichem Delete"
            control={
              <Switch
                size="small"
                checked={prefs.toastDelete}
                onChange={(_, v) => setPrefs((p) => ({ ...p, toastDelete: v }))}
              />
            }
          />
          <SettingRow
            label="Änderungen hervorheben"
            description="Kurzer Highlight-Pulse auf neu geänderten Zeilen"
            badge="Vorschau"
            control={
              <Switch
                size="small"
                checked={prefs.highlightChanges}
                onChange={(_, v) => setPrefs((p) => ({ ...p, highlightChanges: v }))}
              />
            }
          />
        </SectionCard>

        {/* Stammdaten: Anforderungs-Kategorien (mandantenspezifisch) */}
        <SectionCard
          icon={<CategoryOutlinedIcon sx={{ color: 'var(--pm-fg-accent)' }} />}
          title="Anforderungs-Kategorien"
          hint="Mandantenspezifische Stammdaten — frei verwalten, umbenennen, deaktivieren oder löschen"
        >
          <AnforderungKategorienManager />
        </SectionCard>

        {/* Kanban-Darstellung (mandantenspezifischer Standard) */}
        <SectionCard
          icon={<ViewKanbanOutlinedIcon sx={{ color: 'var(--pm-fg-accent)' }} />}
          title="Kanban-Darstellung"
          hint="Mandanten-Standard: ein Board pro Projekt oder pro Arbeitspaket — je Projekt überschreibbar"
        >
          <KanbanSettingsManager />
        </SectionCard>

        {/* Firmenlogo für PDF-Exporte */}
        <SectionCard
          icon={<ImageOutlinedIcon sx={{ color: 'var(--pm-fg-accent)' }} />}
          title="Firmenlogo (PDF-Export)"
          hint="Eigenes Logo statt neutraler Kopfzeile · PNG oder JPEG"
        >
          <PdfLogoSetting key={currentTenant?.id} tenantId={currentTenant?.id} />
        </SectionCard>

        {/* Shortcuts */}
        <SectionCard
          icon={<KeyboardOutlinedIcon sx={{ color: 'var(--pm-fg-accent)' }} />}
          title="Tastatur-Shortcuts"
          hint="Aktive Tastenkombinationen — Read-only"
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: 'var(--pm-fg-subtle)' }}>Kombination</TableCell>
                <TableCell sx={{ color: 'var(--pm-fg-subtle)' }}>Wirkung</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                ['Strg+S', 'Aktuellen Tab speichern (Scope, Status, …)'],
                ['Strg+Shift+R', 'Hard Reload — Browser-Cache umgehen'],
                ['Esc', 'Dialog schließen'],
                ['Enter', 'Im Dialog speichern (wenn Pflichtfelder erfüllt)'],
                ['Entf / Backspace', 'Selektierte Edge im Organigramm löschen'],
                ['Doppelklick (Edge)', 'Verbindung im Organigramm löschen'],
                ['Tab / Shift+Tab', 'Felder im Formular durchwandern'],
              ].map(([combo, action]) => (
                <TableRow key={combo}>
                  <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{combo}</TableCell>
                  <TableCell sx={{ color: 'var(--pm-fg-muted)' }}>{action}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </SectionCard>

        {/* Über */}
        <SectionCard
          icon={<InfoOutlinedIcon sx={{ color: 'var(--pm-fg-accent)' }} />}
          title="Über"
          hint="Version, Stack und Kontext"
        >
          <SettingRow
            label="Frontend-Version"
            description="Aus packages/ui/package.json"
            control={<Chip size="small" label={`v${APP_VERSION}`} sx={{ background: 'var(--pm-bg-deep)', fontFamily: 'monospace' }} />}
          />
          <SettingRow
            label="Backend-Version (pm-api)"
            description="Erwartete kompatible Version"
            control={<Chip size="small" label={`v${BACKEND_VERSION}`} sx={{ background: 'var(--pm-bg-deep)', fontFamily: 'monospace' }} />}
          />
          <SettingRow
            label="Klickdummy-Referenz"
            description="Quelle des UI-Layouts"
            control={<Chip size="small" label={KLICKDUMMY_REF} sx={{ background: 'var(--pm-bg-deep)', fontFamily: 'monospace' }} />}
          />
          <SettingRow
            label="Aktuelle Tenant-ID"
            description="Anzeige zur Diagnose · nicht änderbar"
            control={
              <Chip
                size="small"
                label={currentTenant?.id?.slice(0, 8) ?? '—'}
                sx={{ background: 'var(--pm-bg-deep)', fontFamily: 'monospace' }}
              />
            }
          />
          <SettingRow
            label="Build-Channel"
            description="Aktive Build-Variante"
            control={<PmPill tone="primary">DEV</PmPill>}
          />
        </SectionCard>
      </Box>
    </Box>
  );
}
